import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Loader2, Mail, Phone, Search, User, X } from 'lucide-react'
import { initializeMyPartners } from 'slices/myPartnersSlice'
import { cn } from 'lib/utils'
import FieldShell from '../components/FieldShell'
import FieldAvatar from '../components/FieldAvatar'
import FieldPartnerSheet from '../components/FieldPartnerSheet'
import { mailtoHref, telHref } from 'utils/contact'

interface PartnerRow {
	id: string
	name: string
	/** Company, title, or both. The line under the name. */
	subtitle: string
	image?: string
	mailto: string | null
	tel: string | null
	/** The partnership record itself, for the detail sheet to read in full. */
	raw: any
}

/**
 * The people the operator works with, reachable in one tap.
 *
 * **Accepted partnerships only.** The pending ones are a different job:
 * inviting, chasing and accepting all belong at a desk, and mixing someone who
 * has not replied into a list whose whole promise is "tap to call them" invites
 * a call to somebody who never agreed to take one. The desktop Partners screen
 * keeps that work.
 *
 * Email and phone are handed to the device rather than handled here, for the
 * same reason as the directions handoff: the dialler and the mail client are
 * already signed in and already hold the user's signature. An action whose
 * detail is missing is **hidden rather than disabled** here, because unlike the
 * address pill on Locations there is nothing useful to show in its place.
 */
const FieldPartners = () => {
	const dispatch = useDispatch()
	const [query, setQuery] = useState('')
	/** The partner whose detail sheet is open, or null. */
	const [showing, setShowing] = useState<PartnerRow | null>(null)

	const { entities, ids, isLoading, isInitialized } = useSelector((state: any) => state.myPartners)

	useEffect(() => {
		if (!isInitialized) (dispatch as any)(initializeMyPartners())
	}, [dispatch, isInitialized])

	const partners = useMemo<PartnerRow[]>(() => {
		const rows = (ids || [])
			.map((id: string) => entities[id])
			.filter((row: any) => row && row.isAccepted && !row.isSuspended && !row.isDeleted)
			.map((row: any) => {
				const person = row.partner || {}
				return {
					id: row._id,
					name: person.name || person.firstName || 'Unnamed partner',
					subtitle: [person.title, person.company].filter(Boolean).join(', '),
					image: person.image,
					mailto: mailtoHref(person.email),
					tel: telHref(person.phone),
					raw: row,
				}
			})

		const needle = query.trim().toLowerCase()
		if (!needle) return rows
		return rows.filter(
			(row: PartnerRow) =>
				row.name.toLowerCase().includes(needle) || row.subtitle.toLowerCase().includes(needle),
		)
	}, [entities, ids, query])

	const loading = isLoading && !isInitialized

	return (
		<FieldShell title="Partners">
			<div className="pb-6">
				<div className="sticky top-0 z-20 bg-gray-50 px-3 py-2">
					<div className="relative">
						<Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
						<input
							value={query}
							onChange={e => setQuery(e.target.value)}
							placeholder="Search partners"
							aria-label="Search partners"
							className="w-full rounded-full border border-gray-300 bg-white py-2 pl-9 pr-9 text-base"
							// 16px or larger, or iOS zooms the page on focus.
							style={{ fontSize: '16px' }}
							autoCapitalize="none"
							autoCorrect="off"
							spellCheck={false}
						/>
						{query && (
							<button
								type="button"
								onClick={() => setQuery('')}
								aria-label="Clear search"
								className="absolute right-1 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-gray-400"
							>
								<X className="h-4 w-4" />
							</button>
						)}
					</div>
				</div>

				{loading ? (
					<p className="flex items-center gap-2 px-4 py-6 text-sm text-gray-500">
						<Loader2 className="h-4 w-4 animate-spin" />
						Loading partners…
					</p>
				) : partners.length === 0 ? (
					<div className="m-3 rounded-lg border border-dashed border-gray-300 bg-white p-6 text-center">
						<p className="text-sm font-medium text-gray-800">
							{query ? `Nothing matches "${query}"` : 'No partners yet'}
						</p>
						<p className="mt-1 text-sm text-gray-500">
							{query
								? 'Search looks at names, titles and companies.'
								: 'Invitations are sent and accepted on the desktop site.'}
						</p>
					</div>
				) : (
					<ul className="divide-y divide-gray-100 border-y border-gray-200 bg-white">
						{partners.map((partner: PartnerRow) => (
							<PartnerRowItem key={partner.id} partner={partner} onOpen={setShowing} />
						))}
					</ul>
				)}
			</div>

			<FieldPartnerSheet partner={showing?.raw ?? null} onClose={() => setShowing(null)} />
		</FieldShell>
	)
}

const PartnerRowItem = ({
	partner,
	onOpen,
}: {
	partner: PartnerRow
	onOpen: (partner: PartnerRow) => void
}) => (
	<li className="flex items-center gap-3 px-4 py-3">
		{/*
			The avatar and the name open the detail sheet; the actions beside them
			stay separate controls. One button wrapping the whole row would swallow
			the Email and Call taps.
		*/}
		<button
			type="button"
			onClick={() => onOpen(partner)}
			aria-label={`Show ${partner.name}`}
			className="flex min-w-0 flex-1 items-center gap-3 text-left"
		>
			<FieldAvatar
				src={partner.image}
				name={partner.name}
				fallbackIcon={User}
				label={partner.name}
			/>
			<span className="min-w-0 flex-1">
				<span className="block truncate text-sm font-medium text-gray-900">{partner.name}</span>
				{partner.subtitle && (
					<span className="block truncate text-xs text-gray-500">{partner.subtitle}</span>
				)}
			</span>
		</button>

		{/*
			Anchors, not buttons with handlers. A real href is what lets the OS
			long-press menu work ("Add to Contacts", "Copy Number"), and it is what
			keeps the action available if JavaScript is mid-error.

			An action with no detail behind it is absent rather than greyed: there
			is nothing to read in a disabled Call button, so it would be pure noise
			on a row already tight for width.
		*/}
		{partner.mailto && (
			<ContactAction href={partner.mailto} label={`Email ${partner.name}`} icon={Mail} />
		)}
		{partner.tel && (
			<ContactAction href={partner.tel} label={`Call ${partner.name}`} icon={Phone} />
		)}
	</li>
)

const ContactAction = ({
	href,
	label,
	icon: Icon,
	className,
}: {
	href: string
	label: string
	icon: typeof Mail
	className?: string
}) => (
	<a
		href={href}
		aria-label={label}
		title={label}
		className={cn(
			'inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-primary',
			className,
		)}
	>
		<Icon className="h-4 w-4" />
	</a>
)

export default FieldPartners
